import { Panel } from '../../../layout/Panel';
import PieChartWithNeedle from '../../../layout/components/PieChartWithNeedle';

const dialData = [
    { name: 'Weak', value: 20, color: '#ef4444' },
    { name: 'Watch', value: 20, color: '#f97316' },
    { name: 'Neutral', value: 20, color: '#facc15' },
    { name: 'Positive', value: 20, color: '#84cc16' },
    { name: 'Strong', value: 20, color: '#10b981' },
];

const dialValue = (score, min = -8, max = 8) => {
    const numericValue = Number(score ?? 0);
    return Math.max(0, Math.min(100, ((numericValue - min) / (max - min)) * 100));
};

const formatNumber = (value, decimals = 2) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(decimals) : "-";
};

const formatCompact = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0
        ? new Intl.NumberFormat('en', {
            notation: 'compact',
            maximumFractionDigits: 2,
        }).format(numericValue)
        : "-";
};

const formatMetricValue = (metric) => {
    if (!metric) return "-";
    const numericValue = Number(metric.value);
    if (!Number.isFinite(numericValue)) return "-";
    return metric.metric_key === 'market_cap' ? formatCompact(numericValue) : numericValue.toFixed(2);
};

const metricValue = (metricByKey, key, fallbackValue, formatter = formatNumber) => {
    const formatted = formatMetricValue(metricByKey[key]);
    return formatted !== "-" ? formatted : formatter(fallbackValue);
};

const metricScore = (metricByKey, key, fallbackScore) => {
    return metricByKey[key]?.score ?? fallbackScore;
};

const scoreText = (value) => {
    if (value === null || value === undefined) return "-";
    const numericValue = Number(value ?? 0);
    return `${numericValue > 0 ? "+" : ""}${numericValue.toFixed(0)}`;
};

const scoreTone = (value) => {
    if (value === null || value === undefined) return "border-slate-200 bg-slate-50 text-slate-500";
    const numericValue = Number(value ?? 0);
    if (numericValue > 1) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (numericValue > 0) return "border-lime-200 bg-lime-50 text-lime-700";
    if (numericValue === 0) return "border-slate-200 bg-slate-50 text-slate-600";
    if (numericValue > -2) return "border-amber-200 bg-amber-50 text-amber-700";
    return "border-red-200 bg-red-50 text-red-700";
};

const scoreLabel = (value) => {
    if (value === null || value === undefined) return "Not scored";
    const numericValue = Number(value ?? 0);
    if (numericValue > 1) return "Strong";
    if (numericValue > 0) return "Positive";
    if (numericValue === 0) return "Neutral";
    if (numericValue > -2) return "Watch";
    return "Weak";
};

const Benchmark = ({ label, value }) => (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</div>
        <div className="mt-1 text-sm font-bold text-slate-800">{value === undefined || value === null ? "-" : formatNumber(value)}</div>
    </div>
);

const SummaryTile = ({ label, value, detail }) => (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</div>
        <div className="mt-2 text-2xl font-bold text-slate-950">{value}</div>
        <div className="mt-1 text-sm font-semibold text-slate-500">{detail}</div>
    </div>
);

const FinancialMetricCard = ({ metric }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
            <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{metric.group}</div>
                <h3 className="mt-1 text-lg font-bold text-slate-950">{metric.title}</h3>
            </div>
            <div className={`rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(metric.score)}`}>
                {scoreText(metric.score)}
            </div>
        </div>
        <div className="mt-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm font-semibold leading-6 text-slate-600">
            {metric.definition}
        </div>
        <div className="mt-4 flex items-end justify-between gap-4">
            <div>
                <div className="text-3xl font-bold text-slate-950">{metric.value}</div>
                <div className="mt-1 text-sm font-semibold text-slate-500">{metric.description}</div>
            </div>
            <div className={`rounded-md border px-2 py-1 text-xs font-bold uppercase tracking-[0.12em] ${scoreTone(metric.score)}`}>
                {scoreLabel(metric.score)}
            </div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
            <Benchmark label="Peer Low" value={metric.industryMin} />
            <Benchmark label="Peer Avg" value={metric.industryAverage} />
            <Benchmark label="Peer High" value={metric.industryMax} />
        </div>
        <p className="mt-4 text-sm leading-6 text-slate-600">{metric.note}</p>
    </div>
);

const SupportMetricCard = ({ metric }) => (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-start justify-between gap-3">
            <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{metric.group}</div>
                <h3 className="mt-1 text-base font-bold text-slate-950">{metric.title}</h3>
            </div>
            {metric.score !== undefined && metric.score !== null ? (
                <div className={`rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(metric.score)}`}>
                    {scoreText(metric.score)}
                </div>
            ) : null}
        </div>
        <div className="mt-3 text-2xl font-bold text-slate-950">{metric.value}</div>
        <div className="mt-1 text-sm font-semibold text-slate-500">{metric.description}</div>
        <p className="mt-3 text-sm leading-6 text-slate-600">{metric.definition}</p>
    </div>
);

export const FinanceTab = ({ company, financialMetricsData = [] }) => {
    const financialScore =
        Number(company.financial_metrics_score ?? (
            Number(company.financial_pe_score || 0) +
            Number(company.financial_ae_score || 0) +
            Number(company.financial_pv_score || 0) +
            Number(company.financial_recommendation_score || 0)
        ));

    const metricByKey = financialMetricsData.reduce((carry, metric) => {
        carry[metric.metric_key] = metric;
        return carry;
    }, {});

    const primaryMetrics = [
        {
            group: "Valuation",
            title: "P/E Ratio",
            description: "Price over earnings",
            definition: "Share price divided by earnings per share. Indicates how much investors pay for each unit of company earnings.",
            value: metricValue(metricByKey, 'pe_ratio', company.pe_ratio),
            score: metricScore(metricByKey, 'pe_ratio', company.financial_pe_score),
            industryMin: company.industry?.pe_lower_value,
            industryMax: company.industry?.pe_upper_value,
            industryAverage: company.industry?.pe_average_value,
            note: "Lower relative valuation can improve the score when compared with the available peer group.",
        },
        {
            group: "Leverage",
            title: "A/E Ratio",
            description: "Assets over equity",
            definition: "Assets divided by equity. Indicates balance-sheet leverage and how much assets are funded by equity versus liabilities.",
            value: metricValue(metricByKey, 'ae_ratio', company.ae_ratio),
            score: metricScore(metricByKey, 'ae_ratio', company.financial_ae_score),
            industryMin: company.industry?.ae_lower_value,
            industryMax: company.industry?.ae_upper_value,
            industryAverage: company.industry?.ae_average_value,
            note: "Lower relative A/E can indicate less balance-sheet leverage than peers.",
        },
        {
            group: "Profitability",
            title: "P/V Ratio",
            description: "Profit over volume",
            definition: "Gross profit divided by revenue. Indicates profit intensity relative to the volume of business generated.",
            value: metricValue(metricByKey, 'pv_ratio', company.pv_ratio),
            score: metricScore(metricByKey, 'pv_ratio', company.financial_pv_score),
            industryMin: company.industry?.pv_lower_value,
            industryMax: company.industry?.pv_upper_value,
            industryAverage: company.industry?.pv_average_value,
            note: "Higher relative P/V can indicate stronger profitability against peers.",
        },
        {
            group: "Market View",
            title: "Sentiment",
            description: "Recommendation signal",
            definition: "A market confidence signal based on analyst recommendations, news sentiment, or comparable external opinion indicators.",
            value: metricValue(metricByKey, 'recommendation', company.recommendation),
            score: metricScore(metricByKey, 'recommendation', company.financial_recommendation_score),
            industryMin: company.industry?.recommendation_lower_value,
            industryMax: company.industry?.recommendation_upper_value,
            industryAverage: company.industry?.recommendation_average_value,
            note: "Recommendation and sentiment are mapped onto the same peer score scale as other financial indicators.",
        },
    ];

    const supportMetrics = [
        {
            group: "Leverage",
            title: "Debt-to-Equity",
            description: "Debt over equity",
            definition: "Total debt divided by shareholder equity. Measures financial leverage and balance-sheet risk.",
            value: metricValue(metricByKey, 'debt_to_equity_ratio', company.debt_to_equity_ratio),
            score: metricScore(metricByKey, 'debt_to_equity_ratio', company.financial_debt_to_equity_score),
        },
        {
            group: "Profitability",
            title: "Dividend-to-Earnings",
            description: "Dividend per share over EPS",
            definition: "Dividend per share divided by earnings per share. Shows the share of earnings returned as dividends.",
            value: metricValue(metricByKey, 'dividend_to_earnings_ratio', company.dividend_to_earnings_ratio),
            score: metricScore(metricByKey, 'dividend_to_earnings_ratio', company.financial_dividend_to_earnings_score),
        },
    ];

    const availableMetrics = primaryMetrics.filter((metric) => metric.value !== "-").length;
    const latestPeriod = financialMetricsData.find((metric) => metric.period_end_date)?.period_end_date || company.last_financial_period_end;
    const marketCap = metricValue(metricByKey, 'market_cap', company.market_cap, formatCompact);

    return (
        <div className="space-y-4">
            <Panel className="border border-slate-200 shadow-sm">
                <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)_auto] lg:items-center">
                    <div className="flex justify-center">
                        <PieChartWithNeedle data={dialData} width={200} height={160} value={dialValue(financialScore)} />
                    </div>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">Financial view</p>
                        <h2 className="mt-2 text-2xl font-bold text-slate-950">Financial Score {scoreText(financialScore)}</h2>
                        <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500">
                            The headline score uses the master financial metrics: P/E, A/E, P/V, and sentiment, each pegged against the available peer group.
                        </p>
                    </div>
                    <div className={`w-fit rounded-full border px-4 py-2 text-sm font-bold ${scoreTone(financialScore)}`}>
                        {scoreLabel(financialScore)}
                    </div>
                </div>
                <div className="mt-5 grid gap-3 md:grid-cols-3">
                    <SummaryTile label="Primary Metrics" value={`${availableMetrics}/4`} detail="master financial fields populated" />
                    <SummaryTile label="Market Cap" value={marketCap} detail="equity market value" />
                    <SummaryTile label="Latest Period" value={latestPeriod || "-"} detail="last full quarter" />
                </div>
            </Panel>

            <div className="grid gap-4 xl:grid-cols-2">
                {primaryMetrics.map((metric) => (
                    <FinancialMetricCard key={metric.title} metric={metric} />
                ))}
            </div>

            <Panel className="border border-slate-200 shadow-sm">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">Support metrics</p>
                    <h3 className="mt-1 text-xl font-bold text-slate-950">Balance-sheet and dividend context</h3>
                </div>
                <div className="mt-4 grid gap-4 xl:grid-cols-2">
                    {supportMetrics.map((metric) => (
                        <SupportMetricCard key={metric.title} metric={metric} />
                    ))}
                </div>
            </Panel>
        </div>
    );
};
